import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { Typography } from '@mui/material';
import VersionHistory, { VersionItem } from '../../../components/common/VersionHistory';
import { selectProjectResults } from '../project-details/data-access/project.reducer';
import { AbuseCard } from '../Dashboard/AbuseAI/AbuseCard';
import AISafetyDashboard from './AISafetyDashboard';
import AISafetyCharts from './AISafetyCharts';
import AISafetyEvidenceAccordion from './AISafetyEvidenceAccordion';
import {
  collectSafetyRuns,
  createVersionId,
  filterRunsBySafetyLane,
  formatSafetyVersionTimestamp,
  isNewSafetySchema,
  matchesSafetyRunId,
  sortRunsNewestFirst,
  type AISafetyRun
} from './aiSafety.utils';
import '../Bias/BiasDetectionChartsSection.css';

export interface AISafetyDetectionRef {
  handleHistoryOpen: (type: 'ml' | 'llm') => void;
  setActiveLane: (type: 'ml' | 'llm') => void;
}

interface AISafetyDetectionProps {
  customData?: unknown;
  showInlineVersionControls?: boolean;
  onVersionsReady?: (info: { total: number; runsWithMl: number; runsWithLlm: number }) => void;
}

function runsToVersionItems(runs: AISafetyRun[], selectedRunId: string): VersionItem[] {
  return runs.map((run, index) => {
    const id = createVersionId(run, index);
    const timestamp = formatSafetyVersionTimestamp(run.timestamp) || 'Run';
    return {
      id,
      timestamp,
      date: timestamp,
      modifier: String(run.userId ?? 'System'),
      isCurrent: selectedRunId === id,
      title: String(run.title ?? `AI Safety Run ${index + 1}`)
    };
  });
}

export const AISafetyDetection = forwardRef<AISafetyDetectionRef, AISafetyDetectionProps>(
  ({ customData, showInlineVersionControls = true, onVersionsReady }, ref) => {
    const projectResults = useSelector(selectProjectResults);
    const [showHistory, setShowHistory] = useState(false);
    const [activeHistoryType, setActiveHistoryType] = useState<'ml' | 'llm'>('llm');
    const [activeLane, setActiveLane] = useState<'ml' | 'llm'>('llm');
    const [selectedMlRunId, setSelectedMlRunId] = useState('');
    const [selectedLlmRunId, setSelectedLlmRunId] = useState('');

    const allRuns = useMemo(
      () => sortRunsNewestFirst(collectSafetyRuns(projectResults, customData)),
      [projectResults, customData]
    );

    const mlRuns = useMemo(() => filterRunsBySafetyLane(allRuns, 'ml'), [allRuns]);
    const llmRuns = useMemo(() => filterRunsBySafetyLane(allRuns, 'llm'), [allRuns]);

    useEffect(() => {
      if (allRuns.length === 0) {
        setSelectedMlRunId('');
        setSelectedLlmRunId('');
        onVersionsReady?.({ total: 0, runsWithMl: 0, runsWithLlm: 0 });
        return;
      }

      if (mlRuns.length > 0) {
        const mlExists = mlRuns.some(r => matchesSafetyRunId(r, selectedMlRunId));
        if (!selectedMlRunId || !mlExists) {
          setSelectedMlRunId(createVersionId(mlRuns[0], 0));
        }
      } else {
        setSelectedMlRunId('');
      }

      if (llmRuns.length > 0) {
        const llmExists = llmRuns.some(r => matchesSafetyRunId(r, selectedLlmRunId));
        if (!selectedLlmRunId || !llmExists) {
          setSelectedLlmRunId(createVersionId(llmRuns[0], 0));
        }
      } else {
        setSelectedLlmRunId('');
      }

      if (activeLane === 'llm' && llmRuns.length === 0 && mlRuns.length > 0) {
        setActiveLane('ml');
      } else if (activeLane === 'ml' && mlRuns.length === 0 && llmRuns.length > 0) {
        setActiveLane('llm');
      }

      onVersionsReady?.({
        total: allRuns.length,
        runsWithMl: mlRuns.length,
        runsWithLlm: llmRuns.length
      });
    }, [allRuns, mlRuns, llmRuns, selectedMlRunId, selectedLlmRunId, activeLane, onVersionsReady]);

    const selectedRun = useMemo(() => {
      const mlSelected = mlRuns.find(r => matchesSafetyRunId(r, selectedMlRunId));
      const llmSelected = llmRuns.find(r => matchesSafetyRunId(r, selectedLlmRunId));

      if (activeLane === 'ml') {
        return mlSelected ?? null;
      }

      if (activeLane === 'llm') {
        return llmSelected ?? null;
      }

      return mlSelected ?? llmSelected ?? allRuns[0] ?? null;
    }, [allRuns, mlRuns, llmRuns, selectedMlRunId, selectedLlmRunId, activeLane]);

    const laneRuns = activeLane === 'ml' ? mlRuns : activeLane === 'llm' ? llmRuns : allRuns;

    const historyRuns = activeHistoryType === 'ml' ? mlRuns : llmRuns;
    const activeSelectedId = activeHistoryType === 'ml' ? selectedMlRunId : selectedLlmRunId;

    const versionItems = useMemo(
      () => runsToVersionItems(historyRuns, activeSelectedId),
      [historyRuns, activeSelectedId]
    );

    const handleVersionHistorySelect = (item: VersionItem) => {
      if (activeHistoryType === 'ml') {
        setSelectedMlRunId(item.id);
        setActiveLane('ml');
      } else {
        setSelectedLlmRunId(item.id);
        setActiveLane('llm');
      }
      setShowHistory(false);
    };

    useImperativeHandle(ref, () => ({
      setActiveLane: (type: 'ml' | 'llm') => {
        setActiveLane(type);
      },
      handleHistoryOpen: (type: 'ml' | 'llm') => {
        setActiveHistoryType(type);
        setActiveLane(type);
        setShowHistory(true);
      }
    }));

    const useLegacyView = selectedRun != null && !isNewSafetySchema(selectedRun);

    if (allRuns.length === 0) {
      return (
        <Container fluid className="px-0">
          {showInlineVersionControls ? (
            <Typography variant="h5" className="mb-3">
              AI Safety Detection
            </Typography>
          ) : null}
          <p className="text-muted">No AI Safety evaluation runs found for this project.</p>
        </Container>
      );
    }

    let inlineRuns = allRuns;
    if (llmRuns.length > 0) {
      inlineRuns = llmRuns;
    } else if (mlRuns.length > 0) {
      inlineRuns = mlRuns;
    }

    let inlineSelectedId = activeSelectedId;
    if (llmRuns.length > 0) {
      inlineSelectedId = selectedLlmRunId;
    } else if (mlRuns.length > 0) {
      inlineSelectedId = selectedMlRunId;
    }

    const inlineOnChange = (id: string) => {
      if (llmRuns.some(r => matchesSafetyRunId(r, id))) {
        setSelectedLlmRunId(id);
        setActiveLane('llm');
      }
      if (mlRuns.some(r => matchesSafetyRunId(r, id))) {
        setSelectedMlRunId(id);
        setActiveLane('ml');
      }
    };

    return (
      <Container fluid className="px-0">
        {showInlineVersionControls ? (
          <Row className="align-items-center mb-3 g-2">
            <Col>
              <Typography variant="h5" className="mb-0">
                AI Safety Detection
              </Typography>
            </Col>
            {inlineRuns.length > 1 ? (
              <Col xs="auto">
                <Form.Select
                  size="sm"
                  value={inlineSelectedId}
                  onChange={e => inlineOnChange(e.target.value)}
                  aria-label="Select AI Safety evaluation version"
                  style={{ minWidth: 220 }}
                >
                  {inlineRuns.map((run, index) => {
                    const id = createVersionId(run, index);
                    const ts = formatSafetyVersionTimestamp(run.timestamp);
                    return (
                      <option key={id} value={id}>
                        {String(run.title ?? `Run ${index + 1}`)}
                        {ts ? ` — ${ts.slice(0, 19)}` : ''}
                      </option>
                    );
                  })}
                </Form.Select>
              </Col>
            ) : null}
          </Row>
        ) : null}

        {useLegacyView ? (
          <AbuseCard customData={selectedRun as AISafetyRun} showInlineVersionControls={false} lane={activeLane} />
        ) : selectedRun ? (
          <>
            <AISafetyDashboard run={selectedRun} lane={activeLane} />
            <AISafetyCharts selectedRun={selectedRun} allRuns={laneRuns} lane={activeLane} />
            <AISafetyEvidenceAccordion run={selectedRun} lane={activeLane} />
          </>
        ) : (
          <p className="text-muted small py-3">
            No {activeLane.toUpperCase()} AI Safety evaluation data available for this project.
          </p>
        )}

        <VersionHistory
          open={showHistory}
          onClose={() => setShowHistory(false)}
          versions={versionItems}
          onVersionSelect={handleVersionHistorySelect}
          title={`AI Safety — ${activeHistoryType.toUpperCase()} version history`}
        />
      </Container>
    );
  }
);

AISafetyDetection.displayName = 'AISafetyDetection';

export default AISafetyDetection;
